import { useEffect, useState, useCallback } from "react";
import { Users, Loader2, ShieldCheck, Crown, UserCog, Pencil, User, UserPlus, Power, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import CockpitLayout from "@/components/admin/CockpitLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const GOLD = "#D89A00", INK = "#2C2E33", SLATE = "#777F88", MUTED = "#5B6472", LINE = "#E7E1D6", GREEN = "#1E7E45";

const RANK = { owner: 4, admin: 3, editor: 2, employee: 1 };
const ROLE_LABEL = { owner: "Inhaber", admin: "Admin", editor: "Redaktion", employee: "Team" };
const ROLE_ICON = { owner: Crown, admin: UserCog, editor: Pencil, employee: User };
const roleLabel = (r) => ROLE_LABEL[r] || "Team";

export default function Benutzer() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myRole, setMyRole] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "employee" });
  const [creating, setCreating] = useState(false);

  const myRank = RANK[myRole] || 0;
  const canManage = myRank >= 3; // admin or owner
  const assignable = Object.keys(RANK).filter((r) => RANK[r] < myRank);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/team/members").then(({ data }) => setMembers(data)).catch(() => setMembers([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
    api.get("/org/current").then(({ data }) => { if (data.status === "ok") setMyRole(data.tenant.role); }).catch(() => {});
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) { toast.error("Bitte Name und E-Mail angeben."); return; }
    setCreating(true);
    try {
      await api.post("/team/members", form);
      toast.success("Teammitglied hinzugefügt.");
      setForm({ email: "", name: "", password: "", role: "employee" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setCreating(false); }
  };

  const changeRole = async (m, role) => {
    try { await api.put(`/team/members/${m.user_id}`, { role }); toast.success("Rolle aktualisiert."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const toggleActive = async (m) => {
    try { await api.put(`/team/members/${m.user_id}`, { active: m.status !== "active" }); toast.success("Aktualisiert."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const remove = async (m) => {
    if (!window.confirm(`${m.name || m.email} aus diesem Brocki entfernen?`)) return;
    try { await api.delete(`/team/members/${m.user_id}`); toast.success("Aus Brocki entfernt."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  return (
    <CockpitLayout active="team" title="Team & Benutzer">
      <div className="fundio-page-stack" data-testid="gold-team">
        <div className="fundio-section-head">
          <div>
            <h1 className="fundio-page-title flex items-center gap-2.5" data-testid="team-title">
              <Users size={28} style={{ color: GOLD }} /> Team & Benutzer
            </h1>
            <p className="mt-1 fundio-muted text-[14px]">Alle Personen mit Zugang zu diesem Brocki.</p>
          </div>
        </div>

        {canManage ? (
          <form onSubmit={create} className="fundio-panel" data-testid="add-member-form">
            <h2 className="flex items-center gap-2 text-[17px] font-bold" style={{ color: INK }}><UserPlus size={17} style={{ color: GOLD }} /> Teammitglied hinzufügen</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <input data-testid="member-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-Mail" className="fundio-input" />
              <input data-testid="member-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="fundio-input" />
              <input data-testid="member-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Passwort (nur bei neuem Konto)" className="fundio-input" />
              <select data-testid="member-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="fundio-input">
                {assignable.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
              </select>
            </div>
            <button data-testid="member-submit" type="submit" disabled={creating} className="fundio-action fundio-action--gold mt-4">
              {creating ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Hinzufügen
            </button>
          </form>
        ) : (
          <div className="fundio-panel flex items-center gap-2.5" style={{ borderStyle: "dashed", color: MUTED }} data-testid="team-manage-denied">
            <Lock size={16} style={{ color: SLATE }} /> Nur Inhaber und Admins können Teammitglieder hinzufügen oder verwalten.
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin" style={{ color: GOLD }} /></div>
        ) : (
          <div className="flex flex-col gap-2.5" data-testid="team-members-list">
            {members.map((m) => {
              const self = m.email && user?.email && m.email.toLowerCase() === user.email.toLowerCase();
              const RoleIcon = ROLE_ICON[m.role] || User;
              const inactive = m.status && m.status !== "active";
              const initials = (m.name || m.email || "?").replace(/[^A-Za-zÀ-ÿ ]/g, "").split(" ").filter(Boolean).map((p) => p[0]).slice(0, 2).join("").toUpperCase() || "?";
              const manageable = canManage && !self && myRank > (RANK[m.role] || 0);
              return (
                <div key={m.user_id} className="fundio-panel flex flex-wrap items-center justify-between gap-3" style={{ padding: 16 }} data-testid={`member-${m.user_id}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white" style={{ background: GOLD }}>{initials}</span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="truncate text-[15px] font-bold" style={{ color: INK }}>{m.name || m.email}</h3>
                        {self && <span className="rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ background: "rgba(216,154,0,.18)", color: GOLD }}>DU</span>}
                        {inactive && <span className="rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ background: "rgba(220,38,38,.12)", color: "#DC2626" }}>DEAKTIVIERT</span>}
                      </div>
                      <p className="truncate text-[13px]" style={{ color: SLATE }}>{m.email}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {!inactive && !manageable && m.status === "active" && (
                      <span className="hidden items-center gap-1 text-[11px] font-medium sm:inline-flex" style={{ color: GREEN }}><ShieldCheck size={13} /> Aktiv</span>
                    )}
                    {manageable ? (
                      <>
                        <select data-testid={`member-role-select-${m.user_id}`} value={m.role} onChange={(e) => changeRole(m, e.target.value)} className="fundio-input" style={{ minHeight: 40, width: "auto", padding: "6px 10px", fontSize: 14 }}>
                          {[...assignable, m.role].filter((v, i, a) => a.indexOf(v) === i).map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
                        </select>
                        <button data-testid={`member-toggle-${m.user_id}`} onClick={() => toggleActive(m)} title={m.status === "active" ? "Deaktivieren" : "Aktivieren"}
                          className="fundio-action" style={{ width: 40, minWidth: 40, height: 40, minHeight: 40, padding: 0, color: m.status === "active" ? GREEN : SLATE }}><Power size={15} /></button>
                        <button data-testid={`member-remove-${m.user_id}`} onClick={() => remove(m)} title="Aus Brocki entfernen"
                          className="fundio-action" style={{ width: 40, minWidth: 40, height: 40, minHeight: 40, padding: 0, color: "#903824", borderColor: "#E7B9AF" }}><Trash2 size={15} /></button>
                      </>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide" style={{ background: "rgba(216,154,0,.14)", color: GOLD }} data-testid={`member-role-${m.user_id}`}>
                        <RoleIcon size={12} /> {roleLabel(m.role)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </CockpitLayout>
  );
}
